import {
  CSSProperties,
  FC,
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
} from 'react'

import { clsx } from 'clsx'

import {
  FormControl,
  FormControlProps,
  useFormControlIds,
} from '@tribeplatform/react-ui-kit/FormControl'
import { inputStyles } from '@tribeplatform/react-ui-kit/Input'

import { getEditorPollIds } from '../Composer/extensions/poll/utils.js'
import { useComposer } from '../Composer/hooks/useComposer.js'
import { FieldInputProps } from '../CustomField/type.js'

const Composer = lazy(() =>
  import('../Composer/Composer.js').then(m => ({ default: m.Composer })),
)
const ComposerControls = lazy(() =>
  import('../Composer/components/ComposerControls.js').then(m => ({
    default: m.ComposerControls,
  })),
)
const ComposerProvider = lazy(() =>
  import('../Composer/ComposerProvider.js').then(m => ({
    default: m.ComposerProvider,
  })),
)

type EditorFocusTarget = { focus: () => void }

export type FormControlHtmlEditorProps = FormControlProps & {
  value?: string
  onChange?: (value: string) => void
  className?: string
  style?: CSSProperties
  meta?: FieldInputProps['meta']
  /**
   * Receives a focus handle for the editor, e.g. react-hook-form's `field.ref`.
   */
  inputRef?: (target: EditorFocusTarget) => void
}

const EditorFocusRef = ({
  inputRef,
}: Pick<FormControlHtmlEditorProps, 'inputRef'>) => {
  const { editor } = useComposer()

  // Re-register on every render: react-hook-form drops registered refs on reset().
  useEffect(() => {
    inputRef?.({ focus: () => editor?.commands.focus() })
  })

  return null
}

export const FormControlHtmlEditor: FC<FormControlHtmlEditorProps> = ({
  placeholder,
  name,
  value,
  error,
  invalid,
  label,
  helperText,
  onChange,
  className,
  style,
  meta,
  inputRef,
}) => {
  const ids = useFormControlIds()
  const containerRef = useRef<HTMLDivElement>(null)
  // The editor only exists on the client, so it must point at the label id that
  // is actually in the DOM: after SSR hydration that is the server's useId value.
  const [labelId, setLabelId] = useState<string>()
  useEffect(() => {
    setLabelId(
      containerRef.current?.querySelector(':scope > label')?.id || undefined,
    )
  }, [label])
  const editorAttributes: Record<string, string> = {
    ...(labelId && { 'aria-labelledby': labelId }),
    ...(invalid &&
      error && { 'aria-invalid': 'true', 'aria-describedby': ids.errorId }),
  }

  return (
    <div ref={containerRef} className={clsx('space-y-1', className)}>
      {label && (
        <FormControl.Label id={ids.labelId} htmlFor={name}>
          {label}
        </FormControl.Label>
      )}
      <Suspense fallback={<></>}>
        <ComposerProvider
          content={value}
          placeholder={placeholder}
          editorAttributes={editorAttributes}
          editorOptions={{
            // Seed on create so polls already in the content (e.g. when editing)
            // are reported even if the user never touches this field.
            onCreate: ({ editor }) => {
              meta?.composer?.onPollIdsChange?.(getEditorPollIds(editor))
            },
            onUpdate: ({ editor }) => {
              onChange(editor.isEmpty ? '' : editor.getHTML())

              // instead of prop drilling and passing the meta along the way, we could also use formContext to set value directly, but this approach is more type safe and as we specified it as optional, we guarantee that nowhere will break
              meta?.composer?.onPollIdsChange?.(getEditorPollIds(editor))
            },
          }}
          poll={meta?.composer?.poll}
          key={placeholder}
        >
          <EditorFocusRef inputRef={inputRef} />
          <div
            style={style}
            className={clsx(
              inputStyles({
                invalid,
              }),
              'flex flex-col gap-3 relative',
            )}
          >
            <Composer minLines={8} />
            <ComposerControls />
          </div>
        </ComposerProvider>
      </Suspense>
      <FormControl.Meta
        invalid={invalid}
        error={error}
        helperText={helperText}
        label={label}
        id={ids.errorId}
      />
    </div>
  )
}
